import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Card, CardContent } from '../components/ui/Card';

export function PrivacyPolicy() {
  const { t } = useTranslation();

  return (
    <div className="container-app py-12">
      <div className="mx-auto max-w-3xl">
        <h1 className="mb-8 text-3xl font-bold sm:text-4xl">
          {t('privacy.title', 'Privacy Policy')}
        </h1>

        <Card variant="bordered">
          <CardContent className="prose dark:prose-invert max-w-none">
            <p className="text-sm text-gray-900 dark:text-gray-200">
              Last updated: {new Date().toLocaleDateString()}
            </p>

            <h2>{t('privacy.data_collection', 'What data we collect')}</h2>
            <p>
              When you use Do You Like This Work?, we collect:
            </p>
            <ul>
              <li><strong>Voting data:</strong> Your votes (like/dislike), reasons for your choices, and time taken to vote</li>
              <li><strong>Browser fingerprint:</strong> A hashed identifier to prevent duplicate votes while preserving anonymity</li>
              <li><strong>Location data:</strong> Country and region (provided by Cloudflare) for demographic analysis</li>
              <li><strong>Technical data:</strong> No IP addresses, cookies, or tracking pixels</li>
            </ul>

            <h2>{t('privacy.how_we_use', 'How we use your data')}</h2>
            <p>
              We use the collected data to:
            </p>
            <ul>
              <li>Analyze aesthetic preferences and AI perception biases</li>
              <li>Generate aggregate statistics about voting patterns</li>
              <li>Prevent spam and duplicate voting</li>
              <li>Conduct research on human perception of AI-generated art</li>
            </ul>

            <h2>{t('privacy.data_storage', 'Data storage and security')}</h2>
            <p>
              Your data is stored securely on Cloudflare's infrastructure:
            </p>
            <ul>
              <li>All data is encrypted in transit and at rest</li>
              <li>Browser fingerprints are cryptographically hashed and cannot be reversed</li>
              <li>We do not store any personally identifiable information</li>
              <li>No cookies or third-party trackers are used</li>
            </ul>

            <h2>{t('privacy.data_sharing', 'Data sharing')}</h2>
            <p>
              We do not sell or share individual voting data. Aggregate, anonymized
              statistics may be published for research purposes or shared with the
              research community.
            </p>

            <h2>{t('privacy.your_rights', 'Your rights')}</h2>
            <p>
              Because we do not collect personally identifiable information, we cannot
              identify specific users to delete their data. However, all data is
              anonymized by design, ensuring your privacy is protected from the start.
            </p>

            <h2>{t('privacy.suggestions', 'Image suggestions')}</h2>
            <p>
              When you submit an image suggestion, we collect:
            </p>
            <ul>
              <li>The image URL you provided</li>
              <li>Any context or notes you included</li>
              <li>A hashed fingerprint for rate limiting</li>
            </ul>
            <p>
              This data is used solely to review and potentially add images to the
              collection. Suggestions are not published publicly.
            </p>

            <h2>{t('privacy.children', 'Children\'s privacy')}</h2>
            <p>
              This service is not directed at children under 13. We do not knowingly
              collect data from children. If you believe a child has used this service,
              please contact us.
            </p>

            <h2>{t('privacy.changes', 'Changes to this policy')}</h2>
            <p>
              We may update this privacy policy from time to time. Changes will be
              posted on this page with an updated date. Continued use of the service
              after changes constitutes acceptance of the updated policy.
            </p>

            <h2>{t('privacy.contact', 'Contact')}</h2>
            <p>
              If you have questions about this privacy policy or our data practices,
              please open an issue on our GitHub repository or contact us through
              the project page.
            </p>
          </CardContent>
        </Card>

        <div className="mt-6 text-center">
          <Link
            to="/"
            className="inline-flex items-center gap-1 text-sm text-gray-900 hover:text-accent dark:text-gray-200"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            {t('common.back', 'Back')}
          </Link>
        </div>
      </div>
    </div>
  );
}
